import React, { useState, useEffect } from 'react';
import { array, bool, func, string } from 'prop-types';

import { connect } from 'react-redux';

import { get } from 'lodash';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';

import EsmsDialog from '../../components/EsmsDialog';

import * as ArchitectActions from '../../actions/architectActions';
import * as ApplicationMessageActions from '../../actions/applicationMessageActions';
import * as SiteActions from '../../actions/siteActions';
import * as UiActions from '../../actions/uiActions';

import classes from './CreateEntityModal.module.css';

const ValidationSchema = Yup.object().shape({
    client: Yup.string()
        .required(),
    site: Yup.string()
        .required(),
    name: Yup.string()
        .required(),
    systemType: Yup.string()
        .required()
});

const CreateSystemModal = ({
    close,
    clearErrors,
    clients,
    errors,
    fetchClientSites,
    isLoading,
    open,
    selectedClient,
    selectedSite,
    sites,
    systemTypes,
    title,
    toggleLoading,
    ...props
}) => {

    const [clientSelected, setClientSelected] = useState(false);

    useEffect(() => {
        if (sites.length > 0) {
            setClientSelected(true);
        } else {
            setClientSelected(false);
        }
    }, [sites]);

    const formik = useFormik({
        initialValues: {
            client:     get(selectedClient, 'id', ''),
            site:       get(selectedSite, 'id', ''),
            name:       '',
            systemType: ''
        },
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting }) => {
            const {
                createSystem
            } = props;

            let isCreated;

            const siteId = values.site;


            const body = {
                name:         values.name,
                systemTypeId: values.systemType
            };

            clearErrors();
            setSubmitting(true);

            try {
                if (!isLoading) {
                    toggleLoading(true);
                    isCreated = await createSystem(siteId, body);
                }

                if (isCreated) {
                    setSubmitting(false);
                    toggleLoading(false);

                    handleClose();
                } else {
                    setSubmitting(false);
                    toggleLoading(false);
                }
            } catch (error) {
                setSubmitting(false);
            }
        }
    });

    const handleClientChange = async event => {
        const { value } = event.target;

        if (value !== '') {
            formik.setFieldValue('site', '', false);

            formik.setTouched({
                site: false
            });
    
            setClientSelected(true);
    
            toggleLoading(false);
            await fetchClientSites(value);
    
            formik.handleChange(event);
        }
    };

    const handleClose = () => {
        toggleLoading(false);
        clearErrors();

        setClientSelected(false);
        close();
    };

    return (
        <EsmsDialog
            errors={errors}
            open={open}
            onClose={handleClose}
            title={title}
            onRendered={formik.handleReset}
            onConfirm={formik.handleSubmit}
            disabled={formik.isSubmitting}
            isLoading={isLoading}
            confirmText="Create"
        >
            <div className={classes.formColumn}>
                <div className={classes.labelColumn}>
                    <div className={classes.labelHeader}>
                        <img src="/assets/images/systemTreeIcon.svg" alt="new system icon" />
                        <span>New System</span>
                    </div>
                    <div className={classes.labelContent}>
                        <p>
                            Select the associated Client and Site.
                        </p>
                        <p>
                            Choose a unique name for this system.
                        </p>
                        <p>
                            Type the System Type Id.
                        </p>
                    </div>
                </div>
                <div className={classes.inputColumn}>
                    <div className={classes.inputs}>
                        <FormControl className={classes.inputWrapper}>
                            <InputLabel
                                htmlFor="client"
                                classes={{
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }}
                                error={Boolean(formik.errors.client) && Boolean(formik.touched.client)}
                            >
                                Client
                            </InputLabel>
                            <Select
                                value={formik.values.client}
                                onChange={handleClientChange}
                                className={classes.select}
                                inputProps={{
                                    name:   'client',
                                    id:     'client',
                                    onBlur: formik.handleBlur
                                }}
                                error={Boolean(formik.errors.client) && Boolean(formik.touched.client)}
                            >
                                {
                                    clients.map(type =>
                                        <MenuItem
                                            key={type.id}
                                            value={type.id}
                                        >
                                            {type.name}
                                        </MenuItem>
                                    )
                                }
                            </Select>
                            {formik.touched.client &&
                             formik.errors.client && <FormHelperText className={classes.error}>{formik.errors.client}</FormHelperText>}
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <InputLabel
                                htmlFor="site"
                                classes={{
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }}
                                error={Boolean(formik.errors.site) && Boolean(formik.touched.site)}
                            >
                                Site
                            </InputLabel>
                            <Select
                                value={formik.values.site}
                                onChange={formik.handleChange}
                                className={classes.select}
                                disabled={!clientSelected}
                                inputProps={{
                                    name:   'site',
                                    id:     'site',
                                    onBlur: formik.handleBlur
                                }}
                                error={Boolean(formik.errors.site) && Boolean(formik.touched.site)}
                            >
                                {
                                    sites.map(type =>
                                        <MenuItem
                                            key={type.id}
                                            value={type.id}
                                        >
                                            {type.name}
                                        </MenuItem>
                                    )
                                }
                            </Select>
                            {formik.touched.site &&
                             formik.errors.site && <FormHelperText className={classes.error}>{formik.errors.site}</FormHelperText>}
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="name"
                                name="name"
                                type="text"
                                value={formik.values.name}
                                label="Name"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                FormHelperTextProps={{
                                    classes: {
                                        error: classes.error
                                    }
                                }}
                                error={Boolean(formik.errors.name) && Boolean(formik.touched.name)}
                                helperText={Boolean(formik.touched.name) && formik.errors.name}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <InputLabel
                                htmlFor="systemType"
                                classes={{
                                    error:   classes.error,
                                    focused: classes.labelFocused,
                                    root:    classes.label
                                }}
                                error={Boolean(formik.errors.systemType) && Boolean(formik.touched.systemType)}
                            >
                                System Type
                            </InputLabel>
                            <Select
                                value={formik.values.systemType}
                                onChange={formik.handleChange}
                                className={classes.select}
                                inputProps={{
                                    name:   'systemType',
                                    id:     'systemType',
                                    onBlur: formik.handleBlur
                                }}
                            >
                                {
                                    systemTypes.map(type =>
                                        <MenuItem
                                            key={type.id}
                                            value={type.id}
                                        >
                                            {type.name}
                                        </MenuItem>
                                    )
                                }
                            </Select>
                            {formik.touched.site &&
                             formik.errors.site && <FormHelperText className={classes.error}>{formik.errors.site}</FormHelperText>}
                        </FormControl>
                    </div>
                </div>
            </div>
        </EsmsDialog>
    );
};

CreateSystemModal.defaultProps = {
    clients:     [],
    sites:       [],
    systemTypes: []
};

CreateSystemModal.propTypes = {
    clients:     array,
    close:       func.isRequired,
    open:        bool.isRequired,
    sites:       array,
    title:       string.isRequired,
    systemTypes: array
};

const mapStateToProps = state => {
    return {
        clients:        state.architect.clientNodes,
        companyInfo:    state.companyInfo,
        errors:         state.messages.errors,
        isLoading:      state.ui.isLoading,
        sites:          state.site.sites,
        systemTypes:    state.systemTypes.data,
        selectedClient: state.client.selectedClient,
        selectedSite:   state.site.selectedSite,
        treeReset:      state.architect.treeReset
    };
};

const mapDispatchToProps = {
    ...ArchitectActions,
    ...ApplicationMessageActions,
    ...SiteActions,
    ...UiActions
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateSystemModal);
